import React, { Component, Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import withTranslations from '../../translations/index';

import {
  AlertDialog,
  Button,
  MobileStepper,
  ImageManipulation,
} from '../base';

import { userHasPermission } from '../../utils/permissions';
import { permissions } from '../../constants/permissions';

import {
  renderFileSize,
} from '../FileInput/constants';

import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import DownloadIcon from '@material-ui/icons/SaveAlt';

const styles = theme => ({
  modal: {
    maxHeight: '100vh',

    '&.action': {
      zIndex: '1501 !important',
    },
  },
  title: {
    padding: 0,
  },
  content: {
    maxWidth: '100vw',
    padding: 0,
    maxHeight: 'calc(100vh - 56px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    [theme.breakpoints.up('sm')]: {
      minWidth: 400,
      minHeight: 256,
    },
  },
  controls: {
    height: 54,
    width: '100%',
  },
  fileContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '100%',
    padding: theme.spacing(4, 0),

    [theme.breakpoints.up('sm')]: {
      width: '100%', // 600
      height: 480, // 600 - 120
    },
    '& svg': {
      cursor: 'pointer'
    },
    '& canvas': {
      width: '60% !important',
      height: '85% !important',
      marginBottom: theme.spacing(1),
    }
  },
  fileInfo: {
    height: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 'calc(100vw - 32px)',
    textAlign: 'center',
    margin: `0 ${theme.spacing(2)}px ${theme.spacing()}px`,
    overflow: 'hidden',

    [theme.breakpoints.up('sm')]: {
      maxWidth: 568, // 600 - 32
    },
  },
  fileName: {
    maxWidth: 'calc(100vw - 64px)',
    marginRight: theme.spacing(),
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  downloadIcon: {
    marginLeft: theme.spacing(),
    cursor: 'pointer',
  },
  actions: {
    justifyContent: 'space-around',
  },
  actionButtons: {
    color: theme.palette.error.light,

    '&:hover': {
      background: 'transparent',
    },
  },
  activeDot: {
    background: theme.palette.secondary.main,
  },
});

class FilePreviewModal extends Component {
  state = {
    activeStep: -1,
    activeUuid: null,
  }

  componentDidMount() {
    const { initialIndex, initialUuid } = this.props;

    this.setState({
      activeStep: initialIndex,
      activeUuid: initialUuid,
    })
  }

  handleNext = () => {
    this.setState(prevState =>
      ({ activeStep: prevState.activeStep + 1 }),
      () => this.setState({activeUuid: this.props.files[this.state.activeStep].name})
    )
  };

  handleBack = () => {
    this.setState(prevState =>
      ({ activeStep: prevState.activeStep - 1 }),
      () => this.setState({activeUuid: this.props.files[this.state.activeStep].name})
    );
  };

  renderTitle = (c, gt, maxSteps, activeStep) =>
    <MobileStepper
      className={c.controls}
      steps={maxSteps}
      position="static"
      activeStep={activeStep}
      classes={{ dotActive: c.activeDot }}
      nextButton={
        <Button size="small" onClick={this.handleNext} disabled={activeStep === maxSteps - 1}>
          {gt.next}
          <KeyboardArrowRight />
        </Button>
      }
      backButton={
        <Button size="small" onClick={this.handleBack} disabled={activeStep === 0}>
          <KeyboardArrowLeft />
          {gt.back}
        </Button>
      }
    />

    // At this moment we will be displaying only image previews, so this is obsolete.
    // However, this req is very susceptible to change, so we'll keep this method commented out
  // renderProperFile = (type, src, name) => {
  //   // Max dimensions are 600x480 (or viewport width)
  //   const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
  //   const objectWidth = vw > 960 ? 600 : vw;
  //   const videoHeight = vw > 960 ? 600 / 16 * 9 : objectWidth / 16 * 9 ;
  //   const objectHeight = vw > 600 ? 480 : objectWidth;
  //
  //   if (type && type.startsWith('video')) return <video width={objectWidth} height={videoHeight} controls><source src={src} type={type} /></video>
  //   if (type && type.startsWith('audio')) return <audio controls src={src}></audio>
  //   if (type && type.startsWith('image')) return <img src={src} alt={name} />
  //
  //   return <object width={objectWidth} height={objectHeight} type={type} data={src}>
  //             <p style={{margin: 16}}>Your web browser doesn't have required plugin, or the file size is too large for a preview. Instead you can <a href={src} download={name}>click here to download the file.</a></p>
  //          </object>
  // }

  getAdditionalActions = fileName => {
    const { t, isAdditional, isActionMode, openModal, shouldSaveForm, isSubmitting } = this.props;

    if (isAdditional && !isActionMode) {
      return shouldSaveForm
        ? []
        : [{
            id: 'moveFile',
            text: t.moveFileButtonText,
            onClick: () => openModal('moveFileDialog', fileName),
            disabled: isSubmitting,
          }]
    }
  }

  render() {
    const { activeStep, activeUuid } = this.state;
    const {
      classes: c,
      gt,
      files,
      onClose,
      open,
      openModal,
      isActionMode,
      downloadFile,
      rotateImageEndpoint,
      updateFilesAfterUpload,
      user,
    } = this.props;
    const fileToDisplay = activeStep >= 0 && files[activeStep];
    const fileName = fileToDisplay && fileToDisplay.name;
    const fileSize = fileToDisplay && fileToDisplay.size;
    const maxSteps = files.length;
    const thumbSrc = fileToDisplay && fileToDisplay.thumbnailUrl;

    return (
      <AlertDialog
        title={this.renderTitle(c, gt, maxSteps, activeStep)}
        modalClasses={{root: classNames(c.modal, isActionMode ? 'action' : ''), paperScrollPaper: c.modal}}
        titleClasses={{root: c.title}}
        contentClasses={{root: c.content}}
        actionsClasses={{root: c.actions}}
        actionButtonsClasses={{textSecondary: c.actionButtons}}
        open={open}
        onBackdropClick={onClose}
        confirmButtonText={gt.close}
        cancelButtonText={gt.delete}
        cancelHidden={isActionMode}
        handleConfirm={onClose}
        handleCancel={() => openModal('confirmDeleteFile', activeUuid)}
        additionalActions={this.getAdditionalActions(fileName)}
      >
        {
          fileToDisplay &&
          <Fragment>
            <div className={c.fileContainer}>
              <ImageManipulation
                thumbSrc={thumbSrc}
                fileName={fileName}
                rotateImageEndpoint={rotateImageEndpoint}
                updateFilesAfterUpload={updateFilesAfterUpload}
              />
            </div>
            <div className={c.fileInfo}>
              <div className={c.fileName}>{fileName || gt.unknown}</div>
              {renderFileSize(fileSize)}
              {
                user && userHasPermission(user, permissions.DOWNLOAD_PICTURES) &&
                <DownloadIcon className={c.downloadIcon} onClick={() => downloadFile(fileName)} color='secondary' />
              }
            </div>
          </Fragment>
        }
      </AlertDialog>
    );
  }
}

export default withStyles(styles)(withTranslations(FilePreviewModal, 'FilePreviewModal'))



// WEBPACK FOOTER //
// ./src/components/FilePreviewModal/FilePreviewModal.js